import { computed } from 'vue';
import { hasValidationError } from '@/composables/useValidationState';

let generatedControlId = 0;

// Note: selectPadding entries carry no block padding on purpose — selects are
// fixed height (h-control-*) and vertically center their text natively; py-*
// only shrinks the content box below the line height and clips descenders.
const sizeClasses = {
  xs: {
    control: 'app-control-theme app-control-theme--xs h-control-xs rounded-theme-sm text-xs',
    inputPadding: 'px-control-x-xs py-control-y-xs',
    inputPaddingLeftIcon: 'pl-8',
    inputPaddingRightIcon: 'pr-8',
    inputPaddingLeftText: 'pl-9',
    inputPaddingRightText: 'pr-9',
    icon: 'h-3.5 w-3.5',
    iconOffsetLeft: 'left-control-x-xs',
    iconOffsetRight: 'right-control-x-xs',
    iconGutter: 'w-[var(--size-control-xs)] justify-center',
    selectPadding: 'pl-control-x-xs pr-8',
    selectPaddingWithPrefix: 'pl-8 pr-8',
    textareaPadding: 'px-control-x-xs py-control-y-xs',
    choice: 'h-4 w-4',
    choiceIcon: 'h-3.5 w-3.5',
    choiceText: 'text-xs',
    fileButton: 'px-control-x-xs py-control-y-xs text-xs',
  },
  sm: {
    control: 'app-control-theme app-control-theme--sm h-control-sm rounded-theme-sm text-sm',
    inputPadding: 'px-control-x-sm py-control-y-sm',
    inputPaddingLeftIcon: 'pl-9',
    inputPaddingRightIcon: 'pr-9',
    inputPaddingLeftText: 'pl-10',
    inputPaddingRightText: 'pr-10',
    icon: 'h-4 w-4',
    iconOffsetLeft: 'left-control-x-sm',
    iconOffsetRight: 'right-control-x-sm',
    iconGutter: 'w-[var(--size-control-xs)] justify-center',
    selectPadding: 'pl-control-x-sm pr-9',
    selectPaddingWithPrefix: 'pl-9 pr-9',
    textareaPadding: 'px-control-x-sm py-control-y-sm',
    choice: 'h-[18px] w-[18px]',
    choiceIcon: 'h-[15px] w-[15px]',
    choiceText: 'text-sm',
    fileButton: 'px-control-x-sm py-control-y-sm text-sm',
  },
  md: {
    control: 'app-control-theme app-control-theme--md h-control-md rounded-theme-sm text-base',
    inputPadding: 'px-control-x-md py-control-y-md',
    inputPaddingLeftIcon: 'pl-10',
    inputPaddingRightIcon: 'pr-10',
    inputPaddingLeftText: 'pl-11',
    inputPaddingRightText: 'pr-11',
    icon: 'h-[18px] w-[18px]',
    iconOffsetLeft: 'left-control-x-md',
    iconOffsetRight: 'right-control-x-md',
    iconGutter: 'w-[var(--size-control-xs)] justify-center',
    selectPadding: 'pl-control-x-md pr-10',
    selectPaddingWithPrefix: 'pl-10 pr-10',
    textareaPadding: 'px-control-x-md py-control-y-md',
    choice: 'h-5 w-5',
    choiceIcon: 'h-4 w-4',
    choiceText: 'text-sm',
    fileButton: 'px-control-x-md py-control-y-md text-sm',
  },
  lg: {
    control: 'app-control-theme app-control-theme--lg h-control-lg rounded-theme-sm text-base',
    inputPadding: 'px-control-x-lg py-control-y-lg',
    inputPaddingLeftIcon: 'pl-11',
    inputPaddingRightIcon: 'pr-11',
    inputPaddingLeftText: 'pl-12',
    inputPaddingRightText: 'pr-12',
    icon: 'h-5 w-5',
    iconOffsetLeft: 'left-control-x-lg',
    iconOffsetRight: 'right-control-x-lg',
    iconGutter: 'w-[var(--size-control-sm)] justify-center',
    selectPadding: 'pl-control-x-lg pr-11',
    selectPaddingWithPrefix: 'pl-11 pr-11',
    textareaPadding: 'px-control-x-lg py-control-y-lg',
    choice: 'h-[22px] w-[22px]',
    choiceIcon: 'h-[18px] w-[18px]',
    choiceText: 'text-base',
    fileButton: 'px-control-x-lg py-control-y-lg text-sm',
  },
};

const baseSurfaceClass =
  'w-full border outline-none transition duration-150 ease-out placeholder:text-theme-text-muted';

const variantClasses = {
  default:
    'app-control-theme--default border-theme-border bg-theme-surface text-theme-text hover:border-theme-border-strong hover:bg-theme-surface focus:border-theme-focus focus:bg-theme-surface focus:shadow-theme-focus',
  subtle:
    'app-control-theme--subtle border-theme-border/70 bg-theme-surface-raised text-theme-text hover:border-theme-border-strong hover:bg-theme-surface focus:border-theme-focus focus:bg-theme-surface focus:shadow-theme-focus',
  borderless:
    'app-control-theme--borderless border-transparent bg-transparent text-theme-text hover:border-theme-border/60 focus:border-theme-focus focus:shadow-theme-focus',
};

const disabledClass =
  'app-control-theme--disabled cursor-not-allowed border-theme-border/60 bg-theme-bg-subtle text-theme-text-muted opacity-70 hover:border-theme-border/60 hover:bg-theme-bg-subtle';

const readonlyClass =
  'app-control-theme--readonly bg-theme-surface-raised text-theme-text-secondary';

const errorClass =
  'app-control-theme--error border-theme-danger/70 bg-theme-danger/10 text-theme-text hover:border-theme-danger focus:border-theme-danger focus:shadow-[0_0_0_4px_rgb(var(--color-state-danger)/0.14)]';

const successClass =
  'app-control-theme--success border-theme-success/70 bg-theme-success/10 text-theme-text hover:border-theme-success focus:border-theme-success focus:shadow-[0_0_0_4px_rgb(var(--color-state-success)/0.14)]';

export function getFormControlId(prefix = 'form-control') {
  generatedControlId += 1;
  return `${prefix}-${generatedControlId}`;
}

export function mergeDescribedBy(...ids) {
  return [...new Set(ids.filter(Boolean).flatMap((value) => String(value).split(/\s+/)).filter(Boolean))].join(' ') || undefined;
}

export function resolveOptionValue(option, optionValue) {
  if (optionValue && option && typeof option === 'object' && optionValue in option) {
    return option[optionValue];
  }

  if (option && typeof option === 'object') {
    if ('value' in option) {
      return option.value;
    }

    if ('id' in option && !('label' in option)) {
      return option.id;
    }
  }

  return option;
}

export function resolveOptionLabel(option, optionLabel) {
  if (optionLabel && option && typeof option === 'object' && optionLabel in option) {
    return option[optionLabel];
  }

  if (option && typeof option === 'object') {
    if ('label' in option) {
      return option.label;
    }

    if ('name' in option) {
      return option.name;
    }
  }

  return option;
}

export function normalizeChoiceOptions(options, optionLabel = 'label', optionValue = 'value') {
  if (Array.isArray(options)) {
    return options.map((option, index) => ({
      key: `${index}-${String(resolveOptionLabel(option, optionLabel))}`,
      label: resolveOptionLabel(option, optionLabel),
      value: resolveOptionValue(option, optionValue),
    }));
  }

  if (options && typeof options === 'object') {
    return Object.entries(options).map(([value, label]) => ({
      key: value,
      label,
      value,
    }));
  }

  return [];
}

export function useFormControlStyles(props, validationError = computed(() => props.error)) {
  const normalizedSize = computed(() =>
    props.size && sizeClasses[props.size] ? props.size : 'md'
  );
  const tokens = computed(() => sizeClasses[normalizedSize.value]);
  const hasError = computed(() => hasValidationError(validationError.value));
  const hasSuccess = computed(() => !hasError.value && Boolean(props.success));
  const textColor = computed(() => {
    if (hasError.value) {
      return 'text-theme-danger';
    }

    if (hasSuccess.value) {
      return 'text-theme-success';
    }

    return null;
  });
  const variant = computed(() =>
    props.variant && variantClasses[props.variant] ? props.variant : 'default'
  );

  const controlStateClass = computed(() => {
    if (props.disabled) {
      return disabledClass;
    }

    if (hasError.value) {
      return errorClass;
    }

    if (hasSuccess.value) {
      return successClass;
    }

    return variantClasses[variant.value];
  });

  const inputBaseClass = computed(() => {
    const classes = [baseSurfaceClass, tokens.value.control, controlStateClass.value];

    if (props.readonly && !props.disabled) {
      classes.push(readonlyClass);
    }

    return classes;
  });

  const addonTextClass = computed(() =>
    [
      'pointer-events-none text-sm text-theme-text-secondary',
      props.size === 'lg' ? 'text-sm' : '',
    ].join(' ')
  );

  const choiceLabelClass = computed(() =>
    [
      'app-choice-target inline-flex gap-3 rounded-theme-sm text-theme-text-secondary transition-colors duration-150',
      props.disabled ? 'cursor-not-allowed opacity-70' : 'cursor-pointer',
      tokens.value.choiceText,
    ].join(' ')
  );

  return {
    normalizedSize,
    tokens,
    hasError,
    hasSuccess,
    textColor,
    inputBaseClass,
    addonTextClass,
    choiceLabelClass,
  };
}
